import React from "react";
import moment from "moment-timezone";
import { compose, withHandlers } from "recompose";
import { withPrompt } from "@dpdgroupuk/mydpd-ui";
import { withAppApis, withLogonApis, useAppUser } from "../../Context";
import { M } from "../../constants";
import { VersionUtil } from "../../utils";
import * as appVersionApis from "./appVersionApis";

const AppVersion = ({
  validateAppVersion,
  children
}) => {
  const user = useAppUser();
  React.useEffect(() => {
    if (user) {
      validateAppVersion();
    }
  }, [user, validateAppVersion]);
  const savedCallback = React.useRef();
  React.useEffect(() => {
    const getTimeout = () => {
      const lastCheckVersion = appVersionApis.getLastCheckAppVersion();
      return lastCheckVersion && appVersionApis.lastCheckAppVersionTimeout - moment().diff(lastCheckVersion, "seconds") || appVersionApis.lastCheckAppVersionTimeout + 60; // add extra 60 seconds to process previous first request
    };

    const callInTimeout = () => {
      const timeout = getTimeout();
      savedCallback.current = setTimeout(() => {
        validateAppVersion();
        callInTimeout();
      }, timeout * 1000);
    };

    callInTimeout();
    return () => clearTimeout(savedCallback.current);
  }, [savedCallback]);
  return children;
};

export default compose(withPrompt, withAppApis, withLogonApis, withHandlers({
  validateAppVersion: ({
    prompt,
    logonApis
  }) => async () => {
    const appVersion = await appVersionApis.getCurrentVersion();
    const lastCheckAppVersion = appVersionApis.getLastCheckAppVersion();

    if (appVersion && !lastCheckAppVersion) {
      const [{
        data: minVersion
      }, {
        data: expectedVersion
      }] = await Promise.all([logonApis.apps.getReleasesMinVersion(), logonApis.apps.getReleasesExpectedVersion()]);
      const minVersionIsBigger = minVersion && VersionUtil.isBiggerVersion(minVersion.version, appVersion);
      const expectedVersionIsBigger = expectedVersion && VersionUtil.isBiggerVersion(expectedVersion.version, appVersion);

      if (minVersionIsBigger || expectedVersionIsBigger) {
        appVersionApis.setLastCheckAppVersion(appVersionApis.lastCheckAppVersionTimeout, moment().toISOString());

        if (!minVersionIsBigger && expectedVersionIsBigger) {
          if (appVersionApis.isAppVersionNotified()) {
            return;
          }

          return prompt.showConfirmation({
            header: M.UPDATE_MYDPD_APP,
            message: M.UPDATE_MYDPD_APP_MESSAGE,
            closeButtonText: "Postpone",
            confirmButtonText: "Upgrade",
            closeButtonHidden: minVersionIsBigger,
            onReject: () => appVersionApis.setAppVersionNotified(),
            onConfirm: () => appVersionApis.updateAppMinVersion()
          });
        }

        return prompt.showInfo({
          keyboard: false,
          header: M.UPDATE_MYDPD_APP,
          message: M.UPDATE_MYDPD_APP_MESSAGE,
          confirmButtonText: "Upgrade",
          onConfirm: () => appVersionApis.updateAppMinVersion()
        });
      }

      appVersionApis.setLastCheckAppVersion(appVersionApis.lastCheckAppVersionTimeout, moment().toISOString());
    }
  }
}))(AppVersion);